/**
 * Owner-side Site Access DTOs (the project owner reviewing contractor access).
 *
 * Distinct from the contractor-consumption `portal` entity: these are consumed
 * by the ACCOUNT-authenticated owner on mobile to approve/reject join requests
 * and view attendance. Web parity with
 * `app/api/projects/[projectId]/site-join-requests/*`.
 */
import { z } from "zod";
import { InductionLocaleSchema } from "./induction-locale";
import { EventTimeSourceSchema } from "./event-time-provenance";
import { projectInductionTextField } from "../project-induction-limits";

export const OwnerJoinRequestSchema = z.object({
  id: z.string(),
  name: z.string(),
  companyName: z.string(),
  email: z.string(),
  phone: z.string().nullable(),
  tradeRole: z.string().nullable(),
  cscsCardNumber: z.string().nullable(),
  cscsExpiresAt: z.string().datetime().nullable(),
  status: z.enum(["PENDING", "APPROVED", "REJECTED"]),
  submittedAt: z.string().datetime(),
});
export type OwnerJoinRequest = z.infer<typeof OwnerJoinRequestSchema>;

// #2411 server-boundary variants: the mobile app parses the permissive bases;
// the access routes serialise through the Strict forms.
export const OwnerJoinRequestStrict = OwnerJoinRequestSchema.strict();

export const OwnerAttendanceSessionSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  company: z.string().nullable(),
  onSite: z.boolean(),
  checkedInAt: z.string().datetime(),
  checkedOutAt: z.string().datetime().nullable(),
  checkedInReceivedAt: z.string().datetime().nullable().optional(),
  checkedInTimeSource: EventTimeSourceSchema.nullable().optional(),
  checkedOutReceivedAt: z.string().datetime().nullable().optional(),
  checkedOutTimeSource: EventTimeSourceSchema.nullable().optional(),
});
export type OwnerAttendanceSession = z.infer<
  typeof OwnerAttendanceSessionSchema
>;

export const OwnerAttendanceSessionStrict =
  OwnerAttendanceSessionSchema.strict();

export const JoinRequestReviewRequest = z
  .object({
    action: z.enum(["approve", "reject"]),
    note: z.string().trim().max(1000).optional(),
    companyName: z.string().trim().min(1).max(160).optional(),
  })
  .strict();
export type JoinRequestReviewRequest = z.infer<typeof JoinRequestReviewRequest>;

// ---------------------------------------------------------------------------
// Evacuation / muster roll-call (issue #2277)
//
// Wire mirror of `EvacuationView` / `MusterEntryView` in
// `lib/site-access/evacuation.ts` — the ONE place evacuation state is derived.
// The mobile routes serialise field-by-field out of those lib views rather
// than parsing a Prisma row, so a future lib field cannot reach these strict
// schemas and 500 the route (the #2306 class).
// ---------------------------------------------------------------------------

export const OwnerMusterEntrySchema = z.object({
  id: z.string(),
  name: z.string(),
  company: z.string().nullable(),
  phone: z.string().nullable(),
  safe: z.boolean(),
  markedSafeAt: z.string().datetime().nullable(),
});
export type OwnerMusterEntry = z.infer<typeof OwnerMusterEntrySchema>;

/**
 * Header-only view of an evacuation — no muster entries. Used for the
 * "recent evacuations" list, where the client only needs the counts and the
 * roll-call PDF link.
 */
export const OwnerEvacuationSummarySchema = z.object({
  id: z.string(),
  triggeredAt: z.string().datetime(),
  endedAt: z.string().datetime().nullable(),
  total: z.number().int().nonnegative(),
  safeCount: z.number().int().nonnegative(),
  unaccountedCount: z.number().int().nonnegative(),
});
export type OwnerEvacuationSummary = z.infer<
  typeof OwnerEvacuationSummarySchema
>;

/** Full evacuation view: the summary plus the muster roll. */
export const OwnerEvacuationSchema = z.object({
  id: z.string(),
  triggeredAt: z.string().datetime(),
  endedAt: z.string().datetime().nullable(),
  total: z.number().int().nonnegative(),
  safeCount: z.number().int().nonnegative(),
  unaccountedCount: z.number().int().nonnegative(),
  entries: z.array(OwnerMusterEntrySchema),
});
export type OwnerEvacuation = z.infer<typeof OwnerEvacuationSchema>;

/** PATCH body for marking one muster entry safe / unaccounted. */
export const MusterEntryUpdateRequest = z
  .object({ safe: z.boolean() })
  .strict();
export type MusterEntryUpdateRequest = z.infer<typeof MusterEntryUpdateRequest>;

// ---------------------------------------------------------------------------
// MP-20 (#2285): owner Site Access management — portal users, managers,
// combined settings, attendance corrections, audit export.
// ---------------------------------------------------------------------------

const PORTAL_PERSON_TYPES = [
  "WORKER",
  "SUBCONTRACTOR",
  "VISITOR",
  "DELIVERY",
  "CLIENT",
  "CONSULTANT",
  "INSPECTOR",
  "OTHER",
] as const;

const PORTAL_ROLES = ["WORKER", "CONTRACTOR_MANAGER"] as const;

/**
 * Owner-facing portal-user row. `magicToken` carries the invite credential
 * and is REDACTED (null) for callers who cannot manage site access — an
 * ordinary member can see the roster but must never harvest tokens
 * (#2705 review round 3; the server-rendered page has always redacted).
 */
export const OwnerPortalUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  companyName: z.string().nullable(),
  personType: z.enum(PORTAL_PERSON_TYPES),
  role: z.enum(PORTAL_ROLES),
  active: z.boolean(),
  magicToken: z.string().nullable(),
  createdAt: z.string().datetime(),
  /**
   * Project-scoped: can this person sign in and out on THIS project (Advanced
   * plan spec v1.2 section 4.1). Sent on the project roster (GET
   * /access/portal-users); absent on single-user write echoes.
   */
  canSignIn: z.boolean().optional(),
});
// Server-boundary strict variant (#2411): the mobile app parses the
// permissive base; the routes serialise through the Strict form.
export const OwnerPortalUserStrict = OwnerPortalUserSchema.strict();
export type OwnerPortalUser = z.infer<typeof OwnerPortalUserSchema>;

/** POST body: enrol an EXISTING (account-level) portal user into the project. */
export const OwnerPortalUserAssignRequest = z
  .object({ portalUserId: z.string().min(1) })
  .strict();
export type OwnerPortalUserAssignRequest = z.infer<
  typeof OwnerPortalUserAssignRequest
>;

/** POST body: create a portal user (identity + first project access). */
export const OwnerPortalUserCreateRequest = z
  .object({
    name: z.string().trim().min(1).max(160),
    email: z.string().trim().email().max(320).optional().or(z.literal("")),
    phone: z.string().trim().max(40).optional().or(z.literal("")),
    companyName: z.string().trim().min(1).max(160),
    personType: z.enum(PORTAL_PERSON_TYPES).default("WORKER"),
    role: z.enum(PORTAL_ROLES).default("WORKER"),
  })
  .strict();
export type OwnerPortalUserCreateRequest = z.infer<
  typeof OwnerPortalUserCreateRequest
>;

/** PATCH body: edit a portal user. `companyName: ""` clears the company. */
export const OwnerPortalUserUpdateRequest = z
  .object({
    name: z.string().trim().min(1).max(160).optional(),
    email: z.string().trim().email().max(320).optional().or(z.literal("")),
    phone: z.string().trim().max(40).optional().or(z.literal("")),
    companyName: z.string().trim().max(160).optional(),
    personType: z.enum(PORTAL_PERSON_TYPES).optional(),
    role: z.enum(PORTAL_ROLES).optional(),
    active: z.boolean().optional(),
  })
  .strict();
export type OwnerPortalUserUpdateRequest = z.infer<
  typeof OwnerPortalUserUpdateRequest
>;

/** Attendance-notification toggles (one group of the settings surface). */
export const AttendanceNotificationSettingsSchema = z.object({
  notifyCheckIn: z.boolean(),
  notifyCheckOut: z.boolean(),
  notifyGuestArrival: z.boolean(),
  notifyContractorArrival: z.boolean(),
  notifyMissedCheckout: z.boolean(),
});
// Nested inside OwnerSiteAccessSettingsSchema — the strict boundary is
// OwnerSiteAccessSettingsStrict (which re-stricts the whole tree).
export const AttendanceNotificationSettingsStrict =
  AttendanceNotificationSettingsSchema.strict();
export type AttendanceNotificationSettings = z.infer<
  typeof AttendanceNotificationSettingsSchema
>;

/** The combined owner settings view (web: three separate surfaces). */
export const OwnerSiteAccessSettingsSchema = z.object({
  siteAccessEnabled: z.boolean(),
  ramsSignoffRequired: z.boolean(),
  attendanceNotifications: AttendanceNotificationSettingsSchema,
});
export const OwnerSiteAccessSettingsStrict =
  OwnerSiteAccessSettingsSchema.extend({
    attendanceNotifications: AttendanceNotificationSettingsStrict,
  }).strict();
export type OwnerSiteAccessSettings = z.infer<
  typeof OwnerSiteAccessSettingsSchema
>;

/**
 * PATCH body: any subset of the settings groups. Field-group role rules match
 * the web surfaces: `siteAccessEnabled` and `attendanceNotifications` are
 * owner/admin-only; `ramsSignoffRequired` also accepts a delegated
 * site-access manager.
 */
export const OwnerSiteAccessSettingsPatchRequest = z
  .object({
    siteAccessEnabled: z.boolean().optional(),
    ramsSignoffRequired: z.boolean().optional(),
    attendanceNotifications: AttendanceNotificationSettingsSchema.partial().optional(),
  })
  .strict();
export type OwnerSiteAccessSettingsPatchRequest = z.infer<
  typeof OwnerSiteAccessSettingsPatchRequest
>;

/**
 * A person left WITHOUT sign-in access when Site Access was switched on (or an
 * archived Site Access project was restored) and the free Advanced sign-in
 * places could not cover everyone who needed one: nobody is then given a new
 * place automatically (spec v1.2 section 4.3, ruling of 28 Oct 2026: "all or
 * nothing, then ask"). Returned by the settings PATCH as `signInChoiceNeeded`; the owner
 * chooses who gets sign-in (picker UI: Advanced PR 4).
 */
export const SignInChoiceNeededSchema = z
  .object({
    portalUserId: z.string(),
    name: z.string(),
  })
  .strict();
export type SignInChoiceNeeded = z.infer<typeof SignInChoiceNeededSchema>;

/**
 * Where the account stands against its Site Access limits (spec v1.2 section
 * 8): sign-in users vs the effective limit, operational Site Access projects
 * vs the project limit, and a fix-it message per exceeded limit. `null` limits
 * mean none applies (Site Control, or a plan without Site Access). Returned by
 * the settings GET and PATCH as `limits`.
 */
export const SiteAccessLimitStatusSchema = z
  .object({
    signInUsers: z.number().int().nonnegative(),
    signInLimit: z.number().int().nonnegative().nullable(),
    siteAccessProjects: z.number().int().nonnegative(),
    siteAccessProjectLimit: z.number().int().nonnegative().nullable(),
    overSignInLimit: z.boolean(),
    overProjectLimit: z.boolean(),
    messages: z.array(z.string()),
  })
  .strict();
export type SiteAccessLimitStatus = z.infer<typeof SiteAccessLimitStatusSchema>;

/** The settings GET response: the view plus the account's limit status. */
export const OwnerSiteAccessSettingsGetResponseSchema = z.object({
  settings: OwnerSiteAccessSettingsSchema,
  limits: SiteAccessLimitStatusSchema.optional(),
});
export type OwnerSiteAccessSettingsGetResponse = z.infer<
  typeof OwnerSiteAccessSettingsGetResponseSchema
>;

/** The per-person sign-in PATCH response (Advanced plan spec v1.2 4.3). */
export const PortalUserSignInResponseSchema = z.object({
  access: z.object({
    projectId: z.string(),
    portalUserId: z.string(),
    canSignIn: z.boolean(),
  }),
  changed: z.boolean(),
});
export type PortalUserSignInResponse = z.infer<typeof PortalUserSignInResponseSchema>;

/** The settings PATCH response: the full view plus the switch-on outcome. */
export const OwnerSiteAccessSettingsPatchResponseSchema = z.object({
  settings: OwnerSiteAccessSettingsSchema,
  signInChoiceNeeded: z.array(SignInChoiceNeededSchema),
  limits: SiteAccessLimitStatusSchema,
});
export type OwnerSiteAccessSettingsPatchResponse = z.infer<
  typeof OwnerSiteAccessSettingsPatchResponseSchema
>;

/** Owner correction of one recorded attendance session (manual check-out / notes). */
export const OwnerAttendanceCorrectionRequest = z
  .object({
    checkedOutAt: z.string().datetime().nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
  })
  .strict();
export type OwnerAttendanceCorrectionRequest = z.infer<
  typeof OwnerAttendanceCorrectionRequest
>;

/** Delegated per-project site-access manager row (owner view). */
export const OwnerSiteAccessManagerSchema = z
  .object({
    id: z.string(),
    userId: z.string(),
    name: z.string(),
    email: z.string().nullable(),
  })
  .strict();
export type OwnerSiteAccessManager = z.infer<typeof OwnerSiteAccessManagerSchema>;

export const OwnerSiteAccessManagerAssignRequest = z
  .object({ userId: z.string().min(1) })
  .strict();
export type OwnerSiteAccessManagerAssignRequest = z.infer<
  typeof OwnerSiteAccessManagerAssignRequest
>;

/** One line of the Site Access audit log (json format). */
export const OwnerSiteAccessAuditEventSchema = z.object({
  id: z.string(),
  occurredAt: z.string(),
  category: z.string(),
  action: z.string(),
  subject: z.string(),
  company: z.string().nullable(),
  detail: z.string(),
  source: z.string(),
  metadata: z.unknown(),
});
export type OwnerSiteAccessAuditEvent = z.infer<
  typeof OwnerSiteAccessAuditEventSchema
>;

export const OwnerSiteAccessAuditLogSchema = z.object({
  accountId: z.string(),
  projectId: z.string(),
  range: z.object({
    from: z.string().nullable(),
    to: z.string().nullable(),
  }),
  generatedAt: z.string(),
  events: z.array(OwnerSiteAccessAuditEventSchema),
});
export type OwnerSiteAccessAuditLog = z.infer<typeof OwnerSiteAccessAuditLogSchema>;

// ---------------------------------------------------------------------------
// MP-21 (#2286): Smart Gate — entrance QR, poster, gate policy, RAMS override.
// Site Control only.
// ---------------------------------------------------------------------------

/** Poster print configuration (Smart Gate, MP-24) — mirrors the web poster
 * registry's save body. Lives in contracts so the wire-strictness resolver
 * can inspect it (#2411).
 */
export const SmartGatePosterConfigSchema = z.object({
  size: z.enum(["a4", "a3"]),
  fields: z.record(z.string(), z.boolean()),
  customSections: z.array(
    z.object({ heading: z.string().min(1), body: z.string().min(1) }),
  ),
});
export type SmartGatePosterConfig = z.infer<typeof SmartGatePosterConfigSchema>;

export const OwnerEntranceLinkSchema = z.object({
  id: z.string(),
  token: z.string(),
  label: z.string(),
  status: z.enum(["ACTIVE", "DISABLED"]),
  createdAt: z.string().datetime(),
});
// Server-boundary strict variant (#2411) — the entrance routes serialise
// through this; the mobile app parses the permissive base.
export const OwnerEntranceLinkStrict = OwnerEntranceLinkSchema.strict();
export type OwnerEntranceLink = z.infer<typeof OwnerEntranceLinkSchema>;

export const SiteAccessPolicyPatchRequest = z
  .object({
    mode: z.enum(["CONTROLLED", "BALANCED", "OPEN", "TRUSTED_FIRMS"]),
  })
  .strict();
export type SiteAccessPolicyPatchRequest = z.infer<
  typeof SiteAccessPolicyPatchRequest
>;

export const RamsSignoffOverrideRequest = z
  .object({ portalUserId: z.string().min(1) })
  .strict();
export type RamsSignoffOverrideRequest = z.infer<
  typeof RamsSignoffOverrideRequest
>;

// ---------------------------------------------------------------------------
// MP-22 (#2287): owner induction-translation review surface. Wire mirror of
// the web `GET/POST/PUT /api/projects/[projectId]/induction-translations` and
// `lib/site-access/induction-translation.ts` — the builder reviews the
// AI-translated site-induction text per locale.
// ---------------------------------------------------------------------------

/**
 * The translated-field set mirrors `TRANSLATED_SITE_FIELDS` behind
 * `siteFieldsHash` (`lib/site-access/site-fields.ts`), restated field-by-field
 * (not re-exported) because contracts cannot import app lib code. A lockstep
 * unit test pins the two lists together.
 */

/** Translated values actually present on a stored row (absent fields fell back to English). */
export const OwnerInductionTranslationContentSchema = z.object({
  siteRules: z.string().optional(),
  welfareArrangements: z.string().optional(),
  fireStrategy: z.string().optional(),
  firstAidArrangements: z.string().optional(),
  emergencyProcedures: z.string().optional(),
  assemblyPoint: z.string().optional(),
});
export const OwnerInductionTranslationContentStrict =
  OwnerInductionTranslationContentSchema.strict();
export type OwnerInductionTranslationContent = z.infer<
  typeof OwnerInductionTranslationContentSchema
>;

/** One cached translation row for the project's CURRENT site fields. */
export const OwnerInductionTranslationSchema = z.object({
  locale: InductionLocaleSchema,
  content: OwnerInductionTranslationContentSchema,
  // AI = machine translation; BUILDER = reviewed/overridden by the site team.
  source: z.enum(["AI", "BUILDER"]),
  reviewedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
// Server-boundary strict variant (#2411).
export const OwnerInductionTranslationStrict =
  OwnerInductionTranslationSchema.extend({
    content: OwnerInductionTranslationContentStrict,
  }).strict();
export type OwnerInductionTranslation = z.infer<
  typeof OwnerInductionTranslationSchema
>;

/** Locale option for the owner's language picker (GET response). */
export const InductionLocaleOptionSchema = z.object({
  code: InductionLocaleSchema,
  label: z.string(),
  nativeLabel: z.string(),
});
export const InductionLocaleOptionStrict = InductionLocaleOptionSchema.strict();
export type InductionLocaleOption = z.infer<typeof InductionLocaleOptionSchema>;

/** GET response: cached translations for the current site fields + picker data. */
export const OwnerInductionTranslationListResponse = z
  .object({
    siteFieldsHash: z.string(),
    /** `induction_translations` kill switch — the owner UI hides the picker when off. */
    enabled: z.boolean(),
    locales: z.array(InductionLocaleOptionSchema),
    translations: z.array(OwnerInductionTranslationSchema),
  })
  .strict();
export const OwnerInductionTranslationListResponseStrict =
  OwnerInductionTranslationListResponse.extend({
    locales: z.array(InductionLocaleOptionStrict),
    translations: z.array(OwnerInductionTranslationStrict),
  }).strict();
export type OwnerInductionTranslationListResponse = z.infer<
  typeof OwnerInductionTranslationListResponse
>;

/** Response wrapper for POST (generate) and PUT (review). */
export const OwnerInductionTranslationResponse = z
  .object({ translation: OwnerInductionTranslationSchema })
  .strict();
export const OwnerInductionTranslationResponseStrict =
  OwnerInductionTranslationResponse.extend({
    translation: OwnerInductionTranslationStrict,
  }).strict();
export type OwnerInductionTranslationResponse = z.infer<
  typeof OwnerInductionTranslationResponse
>;

/**
 * POST body: generate (or fetch the cached) translation for one locale.
 * English is refused — it is the authoritative source content itself (same
 * refine + message as the web route's inline schema).
 */
export const OwnerInductionTranslationGenerateRequest = z
  .object({
    locale: InductionLocaleSchema.refine((value) => value !== "en", {
      message: "English is the source language",
    }),
  })
  .strict();
export type OwnerInductionTranslationGenerateRequest = z.infer<
  typeof OwnerInductionTranslationGenerateRequest
>;

/**
 * PUT body: review / override the translated text. Mirrors the web route's
 * `InductionTranslationOverrideSchema` (lib) — trimmed, non-empty per present
 * field; at least one field is required (checked route-side with the web's
 * error copy, matching the web's route-level emptiness check).
 */
export const OwnerInductionTranslationReviewRequest = z
  .object({
    locale: InductionLocaleSchema.refine((value) => value !== "en", {
      message: "English is the source language",
    }),
    content: z
      .object({
        siteRules: z.string().trim().min(1).pipe(projectInductionTextField("siteRules")).optional(),
        welfareArrangements: z.string().trim().min(1).pipe(projectInductionTextField("welfareArrangements")).optional(),
        fireStrategy: z.string().trim().min(1).pipe(projectInductionTextField("fireStrategy")).optional(),
        firstAidArrangements: z.string().trim().min(1).pipe(projectInductionTextField("firstAidArrangements")).optional(),
        emergencyProcedures: z.string().trim().min(1).pipe(projectInductionTextField("emergencyProcedures")).optional(),
        assemblyPoint: z.string().trim().min(1).pipe(projectInductionTextField("assemblyPoint")).optional(),
      })
      .strict(),
  })
  .strict();
export type OwnerInductionTranslationReviewRequest = z.infer<
  typeof OwnerInductionTranslationReviewRequest
>;
